import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import NavBar from "../components/NavBar/NavBar";
import { productStages } from "../constants/productStages";
import { FadeIn, AnimatedDiv } from "../components/ui/Motion";
import { SLIDE_RIGHT_VARIANTS } from "../constants/animations";
import useWindowSize from "../Hooks/useWindowSize";
import { useTranslation } from "react-i18next";


const ProductEvolutionContent = ({ currentStage }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-lg">
      <FadeIn
        as="h2"
        className="text-4xl font-bold mb-4"
        key={currentStage}
        duration={0.5}
      >
        {t(`magic.stage${currentStage + 1}Title`)}
      </FadeIn>
      <FadeIn
        as="p"
        className="text-xl"
        key={`p-${currentStage}`}
        delay={0.2}
        duration={0.5}
      >
        {t(`magic.stage${currentStage + 1}Desc`)}
      </FadeIn>
    </div>
  );
};

const ProductStageImage = ({ stage, isActive }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const scale = width < 768 ? stage.scale : "scale-100";

  return (
    <AnimatedDiv
      initial="hidden"
      animate={isActive ? "visible" : "hidden"}
      variants={SLIDE_RIGHT_VARIANTS}
      transition={{ duration: 0.5 }}
    >
      {stage.type === "image" ? (
        <img src={stage.src} alt={t(`magic.stage${stage.id}Alt`)} className="w-full h-full" />
      ) : stage.type === "video" ? (
        <video
          src={stage.src}
          muted
          autoPlay
          className={`w-full h-full ${scale}`}
          playsInline
        />
      ) : null}
    </AnimatedDiv>
  );
};

const PartnerWithUs = () => {
  const [currentStage, setCurrentStage] = useState(0);
  const containerRef = useRef(null);
  const observerRefs = useRef([]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = Number(entry.target.id);
            setCurrentStage(id - 1);
          }
        });
      },
      { threshold: 0.5 }
    );

    observerRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-grey-500 text-neutral-green-500">
        <main className="pt-20 pb-20 flex" ref={containerRef}>
          <div className="w-1/2 fixed top-20 left-0 h-[calc(100vh-5rem)] flex items-center justify-center p-8 pr-20">
            <ProductEvolutionContent currentStage={currentStage} />
          </div>
          <div className="w-1/2 ml-auto relative">
            <AnimatedDiv
              className="absolute left-0 top-0 w-1 bg-red-500 origin-top z-10"
              style={{ height: lineHeight }}
            />
            {productStages.map((stage, index) => (
              <div
                key={stage.id}
                id={stage.id.toString()}
                ref={(el) => (observerRefs.current[index] = el)}
                className="min-h-screen flex items-center justify-center p-4"
              >
                <ProductStageImage
                  stage={stage}
                  isActive={currentStage === index}
                />
              </div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
};

export default PartnerWithUs;
